import { Layout, Layouts } from 'react-grid-layout'

import { filterMaybe } from '@edge/spark/utils'

import type { DashboardCardDef, WidgetName } from '@edge/spark/io-dashboards'

export const breakpoints = { lg: 1000, md: 900, sm: 768, xs: 580, xxs: 200 }
export const cols = { lg: 6, md: 5, sm: 4, xs: 3, xxs: 2 }

interface DashboardCoord {
    row: number
    col: number
}

export interface DashboardSize {
    breakpoint: string
    cols: number
}
// interface DashboardCardLayout<T> {
//   [size: string]: T
// }

// export interface DashboardCardDef {
//   id: string
//   type: WidgetName
//   config?: GenericWidgetConfig
//   width: number
//   height: number
//   gridLayouts?: DashboardCardLayout<Layout>
// }

export interface DashboardDisplayCardDef {
    x: number
    y: number
    w: number
    h: number
    i: string
    config?: any
    static?: boolean
    widgetName: WidgetName
}

export interface DashboardGridLayouts {
    lg: Layout[]
    md: Layout[]
    sm: Layout[]
    xs: Layout[]
    xss: Layout[]
}

export interface DashboardLayout {
    active?: boolean
    id: string
    name: string
    version?: number
    cards: DashboardCardDef[]
}

const getNextDashboardCoord = (
    currCoord: DashboardCoord,
    card: DashboardCardDef,
    rowColBlackout: number[][],
    maxCols: number,
): DashboardCoord => {
    if (rowColBlackout && rowColBlackout[currCoord.row] && rowColBlackout[currCoord.row].length > 0) {
        if (!rowColBlackout[currCoord.row].find((c) => c === currCoord.col)) {
            if (currCoord.col + card.width <= maxCols) {
                return currCoord
            }

            return getNextDashboardCoord({ row: currCoord.row + 1, col: 0 }, card, rowColBlackout, maxCols)
        }
        return getNextDashboardCoord({ row: currCoord.row, col: currCoord.col + 1 }, card, rowColBlackout, maxCols)
    }
    return currCoord
}

const updateRowCollBlackout = (currCoord: DashboardCoord, card: DashboardCardDef, rowColBlackout: number[][]): void => {
    // INitialize for the rows we have.
    if (rowColBlackout.length < currCoord.row + 1) {
        rowColBlackout.push([])
    }

    let h = 1
    while (h < card.height) {
        const row = currCoord.row + h
        rowColBlackout[row].push(currCoord.col)
        h += 1
    }

    if (!rowColBlackout[currCoord.row]) {
        rowColBlackout[currCoord.row] = []
    }
    let w = 0
    while (w < card.width) {
        const col = currCoord.col + w
        rowColBlackout[currCoord.row].push(col)
        w += 1
    }
}

export const generateLayoutForGridSize = (
    size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg',
    cards: DashboardCardDef[],
    maxCols: number,
): Layout[] => {
    const rowColBlackout: Array<Array<number>> = [[]]
    let currCoord: DashboardCoord = { row: 0, col: 0 }
    let colBuffer = 0

    const cardLayout = cards.map((c, i) => {
        const bufferedCol = i + colBuffer

        // Some cards are wider than 1.  We must add a buffer for thw width of cards over 1
        const nextCol = bufferedCol % maxCols
        // Some cards may be taller than one, we need to add a hight buffer for cards over 1
        const nextRow =
            Math.floor(bufferedCol / maxCols) > currCoord.row ? Math.floor(bufferedCol / maxCols) : currCoord.row
        // if (size === 'lg') console.log(`C-${i}-${bufferedCol} BU : ${c.type}, width: ${c.width} : {${nextRow},${nextCol}} `)

        currCoord = getNextDashboardCoord({ row: nextRow, col: nextCol }, c, rowColBlackout, maxCols)
        updateRowCollBlackout(currCoord, c, rowColBlackout)

        // if (size === 'lg') console.log(`C-${i} AU : ${c.type}, width: ${c.width} : {${currCoord.row},${currCoord.col}} `)
        const cardDetail = {
            x: currCoord.col,
            y: currCoord.row,
            w: c.width,
            h: c.height,
            i: c.id,
            static: false,
            config: c.config,
            widgetName: c.type,
        }

        colBuffer += c.width - 1
        return cardDetail
    })
    return cardLayout
}

export const generateLayoutsForGrid = (cards: DashboardCardDef[], maxCols: number): Layouts => {
    const allLayouts = {
        lg: generateLayoutForGridSize('lg', cards, cols.lg),
        md: generateLayoutForGridSize('md', cards, cols.md),
        sm: generateLayoutForGridSize('sm', cards, cols.sm),
        xs: generateLayoutForGridSize('xs', cards, cols.xs),
        xxs: generateLayoutForGridSize('xxs', cards, cols.xxs),
    }

    return allLayouts as Layouts
}

export const getUpdatedDashboardCards = (layouts: Layouts, cards: DashboardCardDef[]): DashboardCardDef[] => {
    const reactGridLayoutWidth = document.getElementsByClassName('react-grid-layout')[0].clientWidth

    const sortedBreakpointEntires = Object.entries(breakpoints).sort((a, b) => b[1] - a[1])

    const maybeBreakPointEntryIndex = sortedBreakpointEntires.findIndex((x) => x[1] <= reactGridLayoutWidth)

    const breakPointEntryIndex =
        maybeBreakPointEntryIndex === -1 ? sortedBreakpointEntires.length - 1 : maybeBreakPointEntryIndex

    const currentBreakpointKey = sortedBreakpointEntires[breakPointEntryIndex][0]

    const currentBreakpointLayout = layouts[currentBreakpointKey]

    const sortedLayoutCardsXY = currentBreakpointLayout.sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y))

    const sortedCards = sortedLayoutCardsXY
        .map((slcxy) => {
            const foundCard = cards.find((c) => slcxy.i === c.id)
            // if (!foundCard) {
            //   throw new Error(`Could not find card: ${slcxy.i}`)
            // }
            return foundCard
        })
        .filter(filterMaybe)

    return sortedCards
}
